import { useNavigate } from 'react-router'
import { Routes } from '../../routes.ts'
import { useGetBeaconRunCoverage } from '../../utils/api-client/api-hooks.ts'
import { ErrorDetail } from '../../utils/error-handling/error-detail.tsx'
import { ErrorBoundaryFallback } from '../error-boundaries/error-boundary-fallback.tsx'
import { Spinner } from '../spinner.tsx'
import * as styles from './beacon-run-coverage.css.ts'
import { BeaconCoverageView } from './coverage-views/beacon-coverage-view.tsx'
import { useScanVerifiedFindings } from './coverage-views/verified-findings.ts'

export type BeaconRunCoverageProps = {
  /** A completed run: before it completes the platform has no coverage for it. */
  runId: string
  /** The scan the run's findings became, so the views list them and open each on its scan. */
  scanId?: string
}

/**
 * A completed run's coverage: what it examined, the gaps, the evidence, and the leads it dismissed. Keyed
 * by run alone, so any page that knows the run can show it.
 */
export const BeaconRunCoverage: React.FC<BeaconRunCoverageProps> = ({ runId, scanId }) => {
  const navigate = useNavigate()
  const { data: summary, isLoading, error, refetch, isRefetching } = useGetBeaconRunCoverage({ runId })
  const verifiedFindings = useScanVerifiedFindings({
    scanId,
    onOpen: findingId => scanId && navigate(Routes.ScanDetailsPage.createPath({ scanId, triageFindingId: findingId })),
  })

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading Beacon coverage" />
      </div>
    )
  }
  if (error) {
    return (
      <ErrorBoundaryFallback
        title="Unable to load the run’s coverage"
        detail={<ErrorDetail error={error} />}
        onRetry={() => refetch()}
        isRetrying={isRefetching}
      />
    )
  }
  if (!summary) {
    return <p className={styles.note}>The run’s coverage summary is not ready yet.</p>
  }
  return <BeaconCoverageView summary={summary} verifiedFindings={verifiedFindings} />
}
